import React, { useEffect, useMemo } from 'react';
import Header from './Header/Header';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Footer from './Footer/Footer';
import PageLoader from 'components/PageLoader';
import { useDispatch, useSelector } from 'react-redux';
import { getTags } from 'reduxStore/actions/posts';
import routeConstants from 'routes/route.constants';
import { getProfileDetails } from 'reduxStore/actions/user';
import routesConfig from 'routes/routes.config';
import { ScrollToTop } from 'utils/ScrollToTop';

const Layout = () => {
  const isUserLoggedIn = !!localStorage.getItem('accessToken');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userLoading, currentUser } = useSelector((state) => state.userSlice);

  // Check if current route has isHideLayout: true
  const shouldHideLayout = useMemo(() => {
    const allRoutes = [
      ...routesConfig.public.flatMap(route => route.children || [route]),
      ...routesConfig.private,
    ];
    
    const currentPath = location.pathname;
    
    // Find route that matches current path
    const matchingRoute = allRoutes.find(route => {
      if (!route.path) return false;
      
      // Normalize paths - add leading slash if missing
      const routePath = route.path.startsWith('/') ? route.path : `/${route.path}`;
      
      // Handle exact match
      if (routePath === currentPath) return true;
      
      // Handle dynamic routes (e.g., /courses/:courseId)
      const routePattern = routePath
        .replace(/:[^/]+/g, '[^/]+') // Replace :param with pattern
        .replace(/\//g, '\\/'); // Escape slashes
      const regex = new RegExp(`^${routePattern}$`);
      
      return regex.test(currentPath);
    });
    
    return matchingRoute?.isHideLayout === true;
  }, [location.pathname]);

  useEffect(() => {
    (async () => {
      if (isUserLoggedIn) {
        await dispatch(getTags()).unwrap();
      }
    })();
  }, [isUserLoggedIn]);

  useEffect(() => {
    // Only call getProfileDetails if user is logged in
    if (isUserLoggedIn) {
      dispatch(getProfileDetails()).unwrap().then((response) => {
        if (response?.data?.is_active === false) {
          navigate(`/${routeConstants.DEACTIVE_ACCOUNT}`);
        }
      }).catch((error) => {
       
        // If there's an error (like 401), clear the token and redirect to home
        if (error?.response?.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user_id');
          navigate('/');
        }
      });
    }
  }, [isUserLoggedIn]);

  // If shouldHideLayout is true, render only the Outlet without Header and Footer
  if (shouldHideLayout) {
    return (
      <>
        <PageLoader loading={userLoading} />
        <ScrollToTop />
        <Outlet />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className='px-2 sm:px-6 md:px-8 sm:pb-6 pb-4 md:pb-8 relative'>
        <PageLoader loading={userLoading} />
        <ScrollToTop />
        <Outlet />
        <Footer />
      </div>
    </>
  );
};

export default Layout;
